/**
 * Hook studio — the best hook of each type from the brand's own ads, made into brand-neutral examples (backend
 * hookStudio.ts, docs/CONTENT-STORE.md · Hook studio):
 *
 *   ranked    one hook per type, by paid hook rate (3-s views ÷ impressions) — organic virality only as a labelled
 *             stand-in until ad results are read with the person's own login ("Read ad results")
 *   example   the first 6 s, recast on a fictional presenter on Beatly green, saying a brand-neutral English line
 *   the line  editable; a new line re-voices the example (no new recast)
 *
 * Rates and delivery only — never spend.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Loader2, Mic, RefreshCw, ScanSearch, Sparkles, TriangleAlert, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi, type HookCandidate, type HookExample } from '@/cowork/omniad/api/coworkApi';

const typeName = (id: string) => id.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const pct = (n?: number | null) => (n == null ? '–' : `${(n * 100).toFixed(n < 0.1 ? 1 : 0)}%`);
const compact = (n?: number) => (n == null ? '–' : Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n));

/** The headline number of a hook, in the KPI it was ranked by. */
function Measure({ kpi, p }: { kpi: string; p: HookCandidate['performance'] }) {
  if (kpi === 'paid_hook_rate') {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="flex items-baseline gap-1.5"><span className="text-[20px] font-semibold tabular-nums text-[#101828]">{pct(p.hookRate)}</span><span className="text-[11.5px] text-[#667085]">hook rate</span>
          {p.lift != null && <span className={`rounded-full px-1.5 text-[11px] font-semibold ${p.lift >= 0 ? 'bg-[#ecfdf3] text-[#067647]' : 'bg-[#fef3f2] text-[#b42318]'}`}>{p.lift >= 0 ? '+' : ''}{Math.round(p.lift * 100)}% vs brand</span>}</span>
        <span className="text-[11.5px] tabular-nums text-[#667085]">hold {pct(p.holdRate)} · CTR {pct(p.ctr)} · {compact(p.impressions)} impressions</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex items-baseline gap-1.5"><span className="text-[20px] font-semibold tabular-nums text-[#101828]">{p.viralityLift != null ? `${(1 + p.viralityLift).toFixed(1)}×` : '–'}</span><span className="text-[11.5px] text-[#667085]">virality vs brand peers</span></span>
      <span className="text-[11.5px] text-[#b54708]">Organic stand-in · {p.peers ?? 0} peers — read ad results for hook rate</span>
    </div>
  );
}

/**
 * The first seconds of a clip — what the hook is. Ranked hooks play on hover (creator originals are large files; a
 * grid of them autoplaying would stall the page); library examples are short and have controls, with sound.
 */
function HookLoop({ url, seconds = 6, controls = false }: { url: string; seconds?: number; controls?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const play = () => { const v = ref.current; if (v) { v.currentTime = 0; void v.play().catch(() => undefined); } };
  const stop = () => { const v = ref.current; if (v) { v.pause(); v.currentTime = 0.1; } };
  return (
    <video ref={ref} src={`${url}#t=0.1`} muted={!controls} controls={controls} playsInline preload="metadata"
      onMouseEnter={controls ? undefined : play} onMouseLeave={controls ? undefined : stop}
      onTimeUpdate={() => { const v = ref.current; if (!controls && v && v.currentTime >= seconds) v.currentTime = 0; }}
      className="aspect-[9/16] w-full rounded-lg bg-[#101828] object-cover" />
  );
}

function CandidateCard({ c, kpi, inLibrary, busy, onMake }: { c: HookCandidate; kpi: string; inLibrary: boolean; busy: boolean; onMake: (framing: 'selfie' | 'half-body') => void }) {
  const [framing, setFraming] = useState<'selfie' | 'half-body'>('selfie');
  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-[#eaecf0] bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-[#7a1fbf]">{typeName(c.hookType)}</span>
        {c.handle && <span className="truncate text-[11.5px] text-[#98a2b3]">@{c.handle.replace(/^@/, '')}</span>}
      </div>
      <HookLoop url={c.videoUrl} />
      {c.hookText && <p className="line-clamp-3 text-[13px] leading-snug text-[#344054]">“{c.hookText}”</p>}
      {c.creatorOnCamera === false && <span className="text-[11.5px] text-[#98a2b3]">No one on camera — the presenter has nothing to follow.</span>}
      <Measure kpi={kpi} p={c.performance} />
      {c.campaignName && <span className="truncate text-[11.5px] text-[#98a2b3]">{c.campaignName}</span>}
      <div className="mt-auto flex flex-wrap items-center gap-2">
        {inLibrary ? <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#067647]"><Check className="h-3.5 w-3.5" />In the library</span> : <>
          <div className="flex rounded-full border border-[#d0d5dd] p-0.5" title="The presenter's framing must match the hook's: a selfie hook, or one filmed by a friend">
            {(['selfie', 'half-body'] as const).map((f) => <button key={f} type="button" aria-pressed={framing === f} onClick={() => setFraming(f)} className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${framing === f ? 'bg-[#2e024c] text-white' : 'text-[#475467]'}`}>{f === 'selfie' ? 'Selfie' : 'Half-body'}</button>)}
          </div>
          <button type="button" disabled={busy} onClick={() => onMake(framing)} className="ml-auto inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#2e024c] px-2.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#46236a] disabled:opacity-40">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-[#a9f600]" />}Make it
          </button>
        </>}
      </div>
    </div>
  );
}

const STATUS: Record<HookExample['status'], { label: string; tone: string }> = {
  cut: { label: 'Cut', tone: 'bg-[#f2f4f7] text-[#475467]' },
  recasting: { label: 'Recasting…', tone: 'bg-[#f4ebff] text-[#6941c6]' },
  voicing: { label: 'Voicing…', tone: 'bg-[#f4ebff] text-[#6941c6]' },
  ready: { label: 'Ready', tone: 'bg-[#ecfdf3] text-[#067647]' },
  failed: { label: 'Failed', tone: 'bg-[#fef3f2] text-[#b42318]' },
};

function ExampleCard({ ex, onChanged }: { ex: HookExample; onChanged: () => void }) {
  const [line, setLine] = useState(ex.script?.text || '');
  const [fit, setFit] = useState<{ fits: boolean; needs: number; has: number } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!busy) setLine(ex.script?.text || ''); }, [ex.script?.text, busy]);
  const working = ex.status === 'recasting' || ex.status === 'voicing';
  const st = STATUS[ex.status];
  const run = async (fn: () => Promise<unknown>) => { setBusy(true); try { await fn(); onChanged(); } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); } };
  const saveAndVoice = () => run(async () => {
    const r = await coworkApi.insights.hookScript(ex.id, line);
    setFit(r);
    if (!r.fits) { toast.message(`That line needs ~${r.needs} s; the hook leaves ${r.has} s. Shorten it, then voice it.`); return; }
    // A new line re-voices the recast already made (no new recast); before the recast it is simply kept for it.
    if (ex.avatar?.framing) { await coworkApi.insights.hookVoice(ex.id); toast.success('Line saved — voicing the example'); }
    else toast.success('Line saved — it will be used when the presenter version is made');
  });
  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-[#eaecf0] bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-[#7a1fbf]">{typeName(ex.hookType)}</span>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.tone}`}>{working && <Loader2 className="h-3 w-3 animate-spin" />}{st.label}</span>
      </div>
      {ex.avatarUrl ? <HookLoop url={ex.avatarUrl} controls /> : ex.cutUrl ? <HookLoop url={ex.cutUrl} /> : null}
      {ex.status === 'failed' && ex.error && (
        <p className="flex items-start gap-1.5 text-[12px] text-[#b42318]"><TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />{/higgsfield is not connected/i.test(ex.error) ? 'Higgsfield is not connected for this workspace — connect it from the canvas (Vera rail), then recast.' : ex.error}</p>
      )}
      <Measure kpi={ex.kpi} p={ex.performance} />
      <label className="flex flex-col gap-1">
        <span className="flex items-center gap-1 text-[11.5px] font-semibold text-[#344054]" title="English, brand-neutral — edit it and voice it again"><Mic className="h-3 w-3" />Presenter's line</span>
        <textarea value={line} onChange={(e) => { setLine(e.target.value); setFit(null); }} rows={4} disabled={working}
          placeholder={ex.status === 'cut' || working ? 'Drafted from the creator’s words when the example is made…' : 'No spoken line — a visual hook.'}
          className="resize-none rounded-lg border border-[#d0d5dd] px-2.5 py-1.5 text-[12.5px] leading-snug text-[#101828] outline-none focus:border-[#be43ff] disabled:bg-[#f9fafb]" />
        {ex.script?.original && <span className="line-clamp-2 text-[11px] text-[#98a2b3]" title={ex.script.original}>Creator said: “{ex.script.original}”</span>}
        {fit && !fit.fits && <span className="text-[11.5px] text-[#b54708]">Needs ~{fit.needs} s; the hook leaves {fit.has} s.</span>}
      </label>
      <div className="mt-auto flex items-center gap-2">
        <button type="button" disabled={busy || working || !line.trim() || (line.trim() === (ex.script?.text || '') && ex.status === 'ready')} onClick={() => void saveAndVoice()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#2e024c] px-2.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#46236a] disabled:opacity-40">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5 text-[#a9f600]" />}Voice it
        </button>
        <button type="button" disabled={busy || working} onClick={() => void run(() => coworkApi.insights.hookRecast(ex.id, { framing: (ex.avatar?.framing as 'selfie' | 'half-body') || 'selfie' }))} title="Make the presenter version again"
          className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[12px] font-semibold text-[#475467] hover:bg-[#f2f4f7] disabled:opacity-40"><RefreshCw className="h-3.5 w-3.5" />Recast</button>
      </div>
    </div>
  );
}

export function HookStudio({ brandId }: { brandId?: string }) {
  const q = useQuery({
    queryKey: ['hooks', brandId || 'house'], queryFn: () => coworkApi.insights.hooks(brandId),
    refetchInterval: (x) => (x.state.data?.examples.some((e) => e.status === 'recasting' || e.status === 'voicing') ? 5000 : false),
  });
  const [scanning, setScanning] = useState(false);
  /** What the last scan could not tell (no Meta 3-second views, ads not tied to one video) — said, never papered over. */
  const [notes, setNotes] = useState<string[]>([]);
  const [making, setMaking] = useState<string | null>(null);
  const data = q.data;
  const inLibrary = new Set((data?.examples || []).map((e) => e.productId));

  const scan = async () => {
    setScanning(true);
    try {
      const r = await coworkApi.insights.hooksScan(brandId);
      const posts = r.facts?.read ? `${r.facts.pieces} creator posts and ` : '';
      if (r.paid.read === 'failed') toast.error('Ad results could not be read with your login right now.');
      else if (!r.paid.ads && r.notes?.length) toast.message(r.notes[r.notes.length - 1]);
      else toast.success(`Read ${posts}${r.paid.ads} ads${r.paid.campaigns ? ` from ${r.paid.campaigns} campaigns` : ''} — ${r.paid.linked} matched to Content Hub videos.`);
      setNotes(r.notes || []);
      void q.refetch();
    } catch (e) { toast.error((e as Error).message); } finally { setScanning(false); }
  };
  const make = async (c: HookCandidate, framing: 'selfie' | 'half-body') => {
    setMaking(c.productId);
    try { await coworkApi.insights.hookExample({ productId: c.productId, brandId, recast: true, framing }); toast.success(`${typeName(c.hookType)}: cut — the presenter version takes a few minutes`); void q.refetch(); }
    catch (e) { toast.error((e as Error).message); } finally { setMaking(null); }
  };

  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex max-w-[680px] flex-col gap-1">
          <p className="text-[13.5px] text-[#475467]">The best hook of each type from your ads, cut to its first 6 seconds and recast on a presenter — the same move, no creator, no brand. A library for briefs and new videos.</p>
          {data && (
            <span className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${data.kpi === 'paid_hook_rate' ? 'bg-[#ecfdf3] text-[#067647]' : 'bg-[#fffaeb] text-[#b54708]'}`}>
              {data.kpi === 'paid_hook_rate' ? `Ranked by paid hook rate · ${data.linkedAds} of ${data.paidAds} ads matched to files` : 'Ranked by organic virality for now — read ad results to rank by hook rate'}
            </span>
          )}
        </div>
        <button type="button" disabled={scanning || data?.scan?.ok === false} onClick={() => void scan()} title={data?.scan?.ok === false ? 'Needs a login on this client’s team' : undefined} className="inline-flex items-center gap-1.5 rounded-lg border border-[#d0d5dd] bg-white px-3 py-1.5 text-[13px] font-semibold text-[#344054] hover:bg-[#f9fafb] disabled:opacity-40">
          {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />}Read ad results
        </button>
      </div>

      {data?.scan?.ok === false && (
        // Staff in the back office: the datalake answers a login with its own team's ads only.
        <p className="flex items-start gap-1.5 rounded-xl bg-[#f9fafb] px-4 py-2.5 text-[12.5px] text-[#475467]">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#98a2b3]" />
          {data.scan.why === 'other-team'
            ? 'Ad results are read with a login on this client’s team. You’re signed in as Beatly staff, which only reads Beatly’s own ads — staff access to client ad results is with the data team. Until then, read them from the client’s account; the ranking below uses organic results.'
            : 'Ad results are read with your own Beatly login, and this session doesn’t carry one. Sign in again to read them.'}
        </p>
      )}
      {!!notes.length && (
        <ul className="flex flex-col gap-1 rounded-xl bg-[#fffaeb] px-4 py-2.5 text-[12.5px] text-[#93370d]">
          {notes.map((n) => <li key={n} className="flex items-start gap-1.5"><TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />{n}</li>)}
        </ul>
      )}
      {q.isLoading ? <Loader2 className="h-5 w-5 animate-spin text-[#98a2b3]" /> : <>
        {!!data?.examples.length && (
          <div className="flex flex-col gap-2.5">
            <h2 className="flex items-center gap-1.5 text-[14px] font-semibold text-[#101828]"><UserRound className="h-4 w-4 text-[#7a1fbf]" />Library <span className="font-normal text-[#98a2b3]">· {data.examples.length}</span></h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {data.examples.map((ex) => <ExampleCard key={ex.id} ex={ex} onChanged={() => void q.refetch()} />)}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-2.5">
          <h2 className="text-[14px] font-semibold text-[#101828]">Best hook per type <span className="font-normal text-[#98a2b3]">· {data?.candidates.length || 0}</span></h2>
          {data?.candidates.length ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {data.candidates.map((c) => <CandidateCard key={c.productId} c={c} kpi={data.kpi} inLibrary={inLibrary.has(c.productId)} busy={making === c.productId} onMake={(f) => void make(c, f)} />)}
            </div>
          ) : <p className="rounded-xl bg-[#f9fafb] px-4 py-3 text-[13px] text-[#475467]">No ranked hooks yet for this brand. Read ad results, or tag its creator posts first (Creative intelligence).</p>}
        </div>
      </>}
    </section>
  );
}
